import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/content";

const HERO_LINES = ["Elevating Spaces.", "Engineering", "Trust."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.9, 1]);

  const scrollNext = () =>
    document.getElementById("marquee")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-end overflow-hidden"
      style={{ background: "#0B3A82" }}
    >
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img
          src={IMAGES.hero}
          alt="Modern glass elevator architecture"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0"
        style={{
          opacity: overlayOpacity,
          background:
            "linear-gradient(180deg, rgba(11,58,130,0.70) 0%, rgba(8,42,95,0.82) 55%, rgba(8,42,95,0.97) 100%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-20 pt-32 md:px-12 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-3 text-xs font-body font-semibold uppercase tracking-[0.28em] text-[#D89A18]">
            <span className="h-px w-10 bg-[#D89A18]" />
            Jaipur, Rajasthan · Since Day One
          </span>
        </motion.div>

        <h1 className="font-display text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl">
          {HERO_LINES.map((t, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className="block"
                style={i === 2 ? { color: "#D89A18" } : undefined}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-xl font-body text-base leading-relaxed text-white/80 md:text-lg"
          >
            Safe, reliable and professional elevator solutions — Installation,
            Repair, AMC & Modernization for residential, commercial and hospital
            buildings across Jaipur.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={() =>
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
              }
              className="group flex items-center gap-2 px-7 py-4 font-body text-sm font-semibold uppercase tracking-wider transition-all hover:gap-4"
              style={{ background: "#D89A18", color: "#0B3A82" }}
            >
              Get a Free Quote
              <ArrowUpRight size={18} className="transition-transform group-hover:rotate-45" />
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() =>
                document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
              }
              className="px-7 py-4 font-body text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-white/10"
              style={{ border: "1px solid rgba(255,255,255,0.4)" }}
            >
              Our Services
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={scrollNext}
        aria-label="Scroll down"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-6 z-10 hidden flex-col items-center gap-2 md:right-12 md:flex"
      >
        <span className="text-[10px] font-body uppercase tracking-[0.3em] text-white/60">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown size={18} className="text-[#D89A18]" />
        </motion.span>
      </motion.button>
    </section>
  );
}
